import React from "react"
import type { ReactNode } from "react"
import FontPreview from "./font-preview"
import { separator } from "../select-menu/index"
import type { SelectSeparator } from "../select-menu/index"

export type FontOption = {
  value: string
  label: string
  component: ReactNode
  supportedWeights: number[]
  noImport?: boolean
}

/*
  The fonts in this file are divided into serif, sans-serif, and handwritten
  sections.
*/

export const fontOptions: (FontOption | SelectSeparator)[] = [
  // SERIF
  {
    value: "Alegreya, serif",
    label: "Alegreya",
    component: <FontPreview font="Alegreya" />,
    supportedWeights: [400, 500, 600, 700, 800, 900]
  },
  {
    value: "Ancizar Serif, serif",
    label: "Ancizar Serif",
    supportedWeights: [300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Ancizar Serif" />
  },
  {
    label: "Arial",
    value: "Arial",
    supportedWeights: [100, 400, 700],
    component: <FontPreview font="Arial" noImport />,
    noImport: true
  },
  {
    label: "Arial Black",
    value: "'Arial Black', 'Arial Bold'",
    supportedWeights: [400],
    component: <FontPreview font="Arial Black" noImport />,
    noImport: true
  },
  {
    value: "Arvo, serif",
    label: "Arvo",
    component: <FontPreview font="Arvo" />,
    supportedWeights: [400, 700]
  },
  {
    value: "Bodoni Moda, serif",
    label: "Bodoni Moda",
    supportedWeights: [400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Bodoni Moda" />
  },
  {
    label: "Cactus Classical Serif",
    value: "Cactus Classical Serif, serif",
    supportedWeights: [400],
    component: <FontPreview font="Cactus Classical Serif" />
  },
  {
    label: "Century Gothic",
    value: `'Century Gothic', Avenir, 'Trebuchet MS'`,
    supportedWeights: [400, 700],
    component: <FontPreview font="Century Gothic" noImport />,
    noImport: true
  },
  {
    label: "Cormorant Garamond",
    value: "Cormorant Garamond, serif",
    supportedWeights: [300, 400, 500, 600, 700],
    component: <FontPreview font="Cormorant Garamond" />
  },
  {
    label: "Courier New",
    value: `'Courier New', Courier, monospace`,
    supportedWeights: [400, 700],
    component: <FontPreview font="Courier New" noImport />,
    noImport: true
  },
  {
    label: "DM Serif Display",
    value: "DM Serif Display, serif",
    supportedWeights: [400],
    component: <FontPreview font="DM Serif Display" />
  },
  {
    value: "Domine, serif",
    label: "Domine",
    supportedWeights: [400, 500, 700],
    component: <FontPreview font="Domine" />
  },
  {
    value: "EB Garamond, serif",
    label: "EB Garamond",
    supportedWeights: [400, 500, 600, 700, 800],
    component: <FontPreview font="EB Garamond" />
  },
  {
    value: "Fraunces, serif",
    label: "Fraunces",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Fraunces" />
  },
  {
    value: "Hedvig Letters Serif, serif",
    label: "Hedvig Letters Serif",
    supportedWeights: [400],
    component: <FontPreview font="Hedvig Letters Serif" />
  },
  {
    value: "IBM Plex Serif, serif",
    label: "IBM Plex Serif",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700],
    component: <FontPreview font="IBM Plex Serif" />
  },
  {
    value: "Instrument Serif, serif",
    label: "Instrument Serif",
    supportedWeights: [400],
    component: <FontPreview font="Instrument Serif" />
  },
  {
    value: "Libre Baskerville, serif",
    label: "Libre Baskerville",
    supportedWeights: [400, 700],
    component: <FontPreview font="Libre Baskerville" />
  },
  {
    value: "Lora, serif",
    label: "Lora",
    supportedWeights: [400, 500, 600, 700],
    component: <FontPreview font="Lora" />
  },
  {
    value: "Merriweather, serif",
    label: "Merriweather",
    component: <FontPreview font="Merriweather" />,
    supportedWeights: [300, 400, 700, 900]
  },
  {
    value: "Noto Serif, serif",
    label: "Noto Serif",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Noto Serif" />
  },
  {
    value: "Playfair Display, serif",
    label: "Playfair Display",
    supportedWeights: [400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Playfair Display" />
  },
  {
    value: "PT Serif, serif",
    label: "PT Serif",
    supportedWeights: [400, 700],
    component: <FontPreview font="PT Serif" />
  },
  {
    label: "Red Hat Display",
    value: "Red Hat Display, Helvetica, Arial, sans-serif",
    supportedWeights: [300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Red Hat Display" />
  },
  {
    value: "Roboto Slab, serif",
    label: "Roboto Slab",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Roboto Slab" />
  },
  {
    label: "Times New Roman",
    value: "'Times New Roman', Times, Georgia, serif",
    supportedWeights: [400, 500, 600, 700],
    component: <FontPreview font="Times New Roman" noImport />,
    noImport: true
  },
  {
    value: "Unna, serif",
    label: "Unna",
    supportedWeights: [400, 700],
    component: <FontPreview font="Unna" />
  },
  {
    value: "Vollkorn, serif",
    label: "Vollkorn",
    supportedWeights: [400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Vollkorn" />
  },
  separator,
  // SANS-SERIF
  {
    value: "Albert Sans, sans-serif",
    label: "Albert Sans",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Albert Sans" />
  },
  {
    value: "Archivo, sans-serif",
    label: "Archivo",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Archivo" />
  },
  {
    value: "Archivo Black, sans-serif",
    label: "Archivo Black",
    supportedWeights: [400],
    component: <FontPreview font="Archivo Black" />
  },
  {
    label: "Bricolage Grotesque",
    value: `Bricolage Grotesque, Helvetica, sans-serif`,
    supportedWeights: [200, 300, 400, 500, 600, 700, 800],
    component: <FontPreview font="Bricolage Grotesque" />
  },
  {
    label: "Chakra Petch",
    value: "Chakra Petch, sans-serif",
    supportedWeights: [300, 400, 500, 600, 700],
    component: <FontPreview font="Chakra Petch" />
  },
  {
    label: "Datatype",
    value: "Datatype, sans-serif",
    supportedWeights: [400],
    component: <FontPreview font="Datatype" />
  },
  {
    label: "DM Sans",
    value: "DM Sans, sans-serif",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="DM Sans" />
  },
  {
    value: "Exo, sans-serif",
    label: "Exo",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Exo" />
  },
  {
    label: "Figtree",
    value: `Figtree, sans-serif`,
    supportedWeights: [300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Figtree" />
  },
  {
    label: "Geist",
    value: "Geist, sans-serif",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Geist" />
  },
  {
    label: "Geneva",
    value: "Geneva, Calibri",
    supportedWeights: [400, 700],
    component: <FontPreview font="Geneva" noImport />,
    noImport: true
  },
  {
    label: "Georgia",
    value: "Georgia, Times, 'Times New Roman', serif",
    supportedWeights: [400, 700],
    component: <FontPreview font="Georgia" noImport />,
    noImport: true
  },
  {
    label: "Google Sans Code",
    value: "Google Sans Code, monospace",
    supportedWeights: [300, 400, 500, 600, 700, 800],
    component: <FontPreview font="Google Sans Code" />
  },
  {
    label: "Google Sans Flex",
    value: "Google Sans Flex, sans-serif",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Google Sans Flex" />
  },
  {
    label: "Hanken Grotesk",
    value: "Hanken Grotesk, sans-serif",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Hanken Grotesk" />
  },
  {
    label: "Hedvig Letters Sans",
    value: "Hedvig Letters Sans, sans-serif",
    supportedWeights: [400],
    component: <FontPreview font="Hedvig Letters Sans" />
  },
  {
    value: "IBM Plex Sans, sans-serif",
    label: "IBM Plex Sans",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700],
    component: <FontPreview font="IBM Plex Sans" />
  },
  {
    label: "Instrument Sans",
    value: "Instrument Sans, sans-serif",
    supportedWeights: [400, 500, 600, 700],
    component: <FontPreview font="Instrument Sans" />
  },
  {
    label: "Inter",
    value: "Inter, Helvetica, Arial, sans-serif",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Inter" noImport />,
    noImport: true
  },
  {
    value: "Lato, sans-serif",
    label: "Lato",
    supportedWeights: [100, 300, 400, 700, 900],
    component: <FontPreview font="Lato" />
  },
  {
    label: "Lucida Grande",
    value: "'Lucida Grande', Verdana",
    supportedWeights: [400, 700],
    component: <FontPreview font="Lucida Grande" noImport />,
    noImport: true
  },
  {
    value: "Merriweather Sans, sans-serif",
    label: "Merriweather Sans",
    supportedWeights: [300, 400, 500, 600, 700, 800],
    component: <FontPreview font="Merriweather Sans" />
  },
  {
    label: "Mona Sans",
    value: "Mona Sans, sans-serif",
    supportedWeights: [200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Mona Sans" />
  },
  {
    value: "Montserrat, sans-serif",
    label: "Montserrat",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Montserrat" />
  },
  {
    value: "Noto Sans, sans-serif",
    label: "Noto Sans",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Noto Sans" />
  },
  {
    value: "Nunito, sans-serif",
    label: "Nunito",
    supportedWeights: [200, 300, 400, 600, 700, 800, 900],
    component: <FontPreview font="Nunito" />
  },
  {
    value: "Open Sans, sans-serif",
    label: "Open Sans",
    supportedWeights: [300, 400, 600, 700, 800],
    component: <FontPreview font="Open Sans" />
  },
  {
    value: "Open Sans Condensed, sans-serif",
    label: "Open Sans Condensed",
    supportedWeights: [300, 700],
    component: <FontPreview font="Open Sans Condensed" weight={300} />
  },
  {
    value: "Oswald, sans-serif",
    label: "Oswald",
    supportedWeights: [200, 300, 400, 500, 600, 700],
    component: <FontPreview font="Oswald" />
  },
  {
    value: "Outfit, sans-serif",
    label: "Outfit",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Outfit" />
  },
  {
    value: "Plus Jakarta Sans, sans-serif",
    label: "Plus Jakarta Sans",
    supportedWeights: [200, 300, 400, 500, 600, 700, 800],
    component: <FontPreview font="Plus Jakarta Sans" />
  },
  {
    label: "Poppins",
    value: `Poppins, sans-serif`,
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Poppins" />
  },
  {
    value: "PT Sans, sans-serif",
    label: "PT Sans",
    supportedWeights: [400, 700],
    component: <FontPreview font="PT Sans" />
  },
  {
    value: "PT Sans Narrow, sans-serif",
    label: "PT Sans Narrow",
    supportedWeights: [400, 700],
    component: <FontPreview font="PT Sans Narrow" />
  },
  {
    value: "Raleway, sans-serif",
    label: "Raleway",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Raleway" />
  },
  {
    value: "Roboto, sans-serif",
    label: "Roboto",
    supportedWeights: [100, 300, 400, 500, 700, 900],
    component: <FontPreview font="Roboto" />
  },
  {
    value: "Roboto Condensed, sans-serif",
    label: "Roboto Condensed",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Roboto Condensed" />
  },
  {
    value: "Rubik, sans-serif",
    label: "Rubik",
    supportedWeights: [300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Rubik" />
  },
  {
    value: "Space Grotesk, sans-serif",
    label: "Space Grotesk",
    supportedWeights: [300, 400, 500, 600, 700],
    component: <FontPreview font="Space Grotesk" />
  },
  {
    label: "TASA Orbiter",
    value: "TASA Orbiter, sans-serif",
    supportedWeights: [400, 500, 600, 700, 800],
    component: <FontPreview font="TASA Orbiter" />
  },
  {
    label: "Verdana",
    value: `Verdana`,
    supportedWeights: [400, 700],
    component: <FontPreview font="Verdana" noImport />,
    noImport: true
  },
  {
    value: "Work Sans, sans-serif",
    label: "Work Sans",
    supportedWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    component: <FontPreview font="Work Sans" />
  },
  separator,
  // HANDWRITTEN
  {
    value: "Aboreto, sans-serif",
    label: "Aboreto",
    supportedWeights: [400],
    component: <FontPreview font="Aboreto" />
  },
  {
    value: "Allison, cursive",
    label: "Allison",
    supportedWeights: [400],
    component: <FontPreview font="Allison" />
  },
  {
    value: "Allura, cursive",
    label: "Allura",
    supportedWeights: [400],
    component: <FontPreview font="Allura" />
  },
  {
    value: "Amatic SC, cursive",
    label: "Amatic SC",
    supportedWeights: [400],
    component: <FontPreview font="Amatic SC" />
  },
  {
    value: "Anton, sans-serif",
    label: "Anton",
    supportedWeights: [400],
    component: <FontPreview font="Anton" />
  },
  {
    value: "Bangers, sans-serif",
    label: "Bangers",
    supportedWeights: [400],
    component: <FontPreview font="Bangers" />
  },
  {
    value: "Dancing Script, cursive",
    label: "Dancing Script",
    supportedWeights: [400, 500, 600, 700],
    component: <FontPreview font="Dancing Script" />
  },
  {
    value: "Oooh Baby, cursive",
    label: "Oooh Baby",
    supportedWeights: [400],
    component: <FontPreview font="Oooh Baby" />
  },
  {
    value: "Permanent Marker, sans-serif",
    label: "Permanent Marker",
    supportedWeights: [400],
    component: <FontPreview font="Permanent Marker" />
  }
]
